import React, { useMemo, useCallback } from "react";
import screenmediaLogo from "../../img/screenmedia-logo.png";
import visionaLogo from "../../img/visiona-logo.png";
import wecastLogo from "../../img/wecast-logo.png";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import styles from "./stylesPdfPortafolio";

const PdfPortafolio = ({ edificios = [], pais = "Chile", imageSrc = {} }) => {
  const logo = useMemo(
    () => ({ Chile: wecastLogo, Perú: visionaLogo, Uruguay: screenmediaLogo })[pais] || visionaLogo,
    [pais]
  );

  const localString = useCallback((p) => ({ Chile: "es-CL", Perú: "es-PE", Uruguay: "es-UY" })[p] || "es-UY", []);

  const totalPersonas = edificios.reduce((sum, e) => sum + (e.personas || 0), 0);
  const totalPantallas = edificios.reduce(
    (sum, e) => sum + (e.pantallas || 0) + (e.totems || 0) + (e.cowork || 0) + (e.espera || 0),
    0
  );

  const fmt = (num) => (num || 0).toLocaleString(localString(pais));

  const totemsText = (e) => {
    const cant = e.totems || e.cowork;
    if (!cant) return null;
    return `${cant} ${cant > 1 ? "Tótems" : "Tótem"} circulación`;
  };

  const esperaText = (e) => {
    if (!e.espera) return null;
    return `${e.espera} ${e.espera > 1 ? "Pantallas" : "Pantalla"} espera`;
  };

  const empresasInfo = (empresaslistado) => {
    if (!empresaslistado || empresaslistado.toUpperCase() === "NO DISPONIBLE") return "Pendiente";
    return `${empresaslistado.split(",").length} empresas`;
  };

  // Agrupar edificios en pares para layout de 2 columnas
  const rows = [];
  for (let i = 0; i < edificios.length; i += 2) {
    rows.push(edificios.slice(i, i + 2));
  }

  // Calcular altura total para PDF continuo (sin paginación)
  // Header(50) + summary(70) + tabla(25 + N*20) + gap(20) + fichas(rows*210) + footer(40) + padding(40)
  const tableH = 25 + edificios.length * 20;
  const fichasH = rows.length * 210;
  const totalHeight = 50 + 70 + tableH + 20 + fichasH + 40 + 40;
  const pageWidth = 842; // A4 landscape width

  return (
    <Document>
      <Page size={[pageWidth, Math.max(totalHeight, 595)]} style={styles.page} wrap={false}>
        {/* Header */}
        <View style={styles.headerBar}>
          <Image style={styles.logo} src={logo} />
          <Text style={styles.headerTitle}>PORTAFOLIO EDIFICIOS SELECCIONADOS</Text>
        </View>

        {/* Resumen */}
        <View style={styles.summaryRow}>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryNumber}>{edificios.length}</Text>
            <Text style={styles.summaryLabel}>Edificios</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryNumber}>{fmt(totalPersonas)}</Text>
            <Text style={styles.summaryLabel}>Personas únicas diarias</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryNumber}>{totalPantallas}</Text>
            <Text style={styles.summaryLabel}>Pantallas totales</Text>
          </View>
        </View>

        {/* Tabla resumen */}
        <View style={styles.listTable}>
          <View style={styles.listHeaderRow}>
            <Text style={[styles.listHeaderCell, { width: "5%" }]}>#</Text>
            <Text style={[styles.listHeaderCell, { width: "35%", textAlign: "left", paddingLeft: 8 }]}>Edificio</Text>
            <Text style={[styles.listHeaderCell, { width: "16%" }]}>Personas</Text>
            <Text style={[styles.listHeaderCell, { width: "14%" }]}>Ascensores</Text>
            <Text style={[styles.listHeaderCell, { width: "15%" }]}>Circulación</Text>
            <Text style={[styles.listHeaderCell, { width: "15%" }]}>Espera</Text>
          </View>
          {edificios.map((e, idx) => (
            <View key={e.id} style={idx % 2 === 0 ? styles.listRow : styles.listRowAlt}>
              <Text style={[styles.listCell, { width: "5%" }]}>{idx + 1}</Text>
              <Text style={[styles.listCellLeft, { width: "35%" }]}>{(e.edificio || "").toUpperCase()}</Text>
              <Text style={[styles.listCell, { width: "16%" }]}>{fmt(e.personas)}</Text>
              <Text style={[styles.listCell, { width: "14%" }]}>{e.pantallas || "-"}</Text>
              <Text style={[styles.listCell, { width: "15%" }]}>{(e.totems || 0) + (e.cowork || 0) || "-"}</Text>
              <Text style={[styles.listCell, { width: "15%" }]}>{e.espera || "-"}</Text>
            </View>
          ))}
        </View>

        {/* Separador */}
        <View style={styles.separator} />

        {/* Fichas técnicas — 2 por fila */}
        {rows.map((row, rowIdx) => (
          <View key={rowIdx} style={styles.fichasRow}>
            {row.map((e) => (
              <View key={e.id} style={styles.fichaCard}>
                <View style={styles.fichaImageCol}>
                  {imageSrc[e.id] ? (
                    <Image style={styles.fichaImage} src={imageSrc[e.id]} />
                  ) : (
                    <View style={styles.fichaImagePlaceholder}>
                      <Text style={styles.fichaPlaceholderText}>Foto pendiente</Text>
                    </View>
                  )}
                </View>
                <View style={styles.fichaInfoCol}>
                  <View style={styles.fichaNombreBar}>
                    <Text style={styles.fichaNombre}>{e.edificio || ""}</Text>
                    {e.estado === "INSTALACIÓN" && <Text style={styles.fichaInstalacion}>INSTALACIÓN</Text>}
                  </View>
                  <Text style={styles.fichaDireccion}>
                    {(e.direccion || "").toUpperCase()}, {e.ubicacion}
                  </Text>
                  <View style={styles.fichaDispositivosRow}>
                    {e.pantallas > 0 && (
                      <View style={styles.fichaDispItem}>
                        <Text style={styles.fichaDispNum}>{e.pantallas}</Text>
                        <Text style={styles.fichaDispLabel}>Ascensores</Text>
                      </View>
                    )}
                    {totemsText(e) && (
                      <View style={styles.fichaDispItem}>
                        <Text style={styles.fichaDispNum}>{e.totems || e.cowork}</Text>
                        <Text style={styles.fichaDispLabel}>Circulación</Text>
                      </View>
                    )}
                    {esperaText(e) && (
                      <View style={styles.fichaDispItem}>
                        <Text style={styles.fichaDispNum}>{e.espera}</Text>
                        <Text style={styles.fichaDispLabel}>Espera</Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.fichaBottomRow}>
                    <View style={styles.fichaPersonas}>
                      <Text style={styles.fichaPersonasNum}>{fmt(e.personas)}</Text>
                      <Text style={styles.fichaPersonasLabel}>personas únicas diarias</Text>
                    </View>
                    <View style={styles.fichaEmpresas}>
                      <Text style={styles.fichaEmpresasNum}>{empresasInfo(e.empresaslistado)}</Text>
                      {e.empresasdestacadas && (
                        <Text style={styles.fichaEmpresasDest}>{e.empresasdestacadas.toUpperCase()}</Text>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            ))}
          </View>
        ))}

        {/* Footer */}
        <View style={styles.fichaPageFooter}>
          <Image style={styles.fichaFooterLogo} src={logo} />
        </View>
      </Page>
    </Document>
  );
};

export default PdfPortafolio;
